// Services/EmployeeServices/TeamAttendanceService.js
// Supervisor/Manager team attendance, scoped to the logged-in employee's direct
// reports. Backed by the /Attendance/Supervisor/* endpoints (per Mudassir's handoff).
import { useState } from "react";
import { message } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { ReportPeriodTypeEnum } from "Constants/Enums/ReportPeriodTypeEnum";

export default function TeamAttendanceService() {
  const [loading, setLoading] = useState(false);

  // Today's attendance for the supervisor's direct reports.
  const fetchToday = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.attendance.supervisorToday);
      return res.data;
    } catch (e) {
      console.error("Error fetching team today attendance:", e);
      message.error("Failed to load today's team attendance");
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Team attendance for a given month/year (per-day records with employee rows).
  const fetchTeamMonth = async (month, year) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.attendance.supervisorTeamByMonthYear(month, year));
      return res.data;
    } catch (e) {
      console.error("Error fetching team month attendance:", e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Team work-life balance report for a given month/year.
  const fetchTeamWLB = async (month, year) => {
    try {
      setLoading(true);
      // Point 13: translate month/year to periodType=Month + referenceDate;
      // response is { period, data } so return the payload.
      const referenceDate =
        month && year ? `${year}-${String(month).padStart(2, "0")}-01` : null;
      const res = await axiosInstance.get(
        API.workLifeBalance.getSupervisorTeamMonthlyReport(
          ReportPeriodTypeEnum.Month,
          referenceDate
        )
      );
      const body = res.data;
      return body && body.data !== undefined ? body.data : body;
    } catch (e) {
      console.error("Error fetching team work-life balance:", e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // One direct report's attendance history (used in the drawer). No shared loading.
  const fetchEmployeeAttendance = async (employeeId, viewType, startDate, endDate) => {
    const res = await axiosInstance.get(
      API.attendance.supervisorEmployeeAttendance(employeeId, viewType, startDate, endDate)
    );
    return res.data;
  };

  // One direct report's work-life balance summary (used in the drawer).
  const fetchEmployeeWLB = async (employeeId, month, year) => {
    // Point 13: translate month/year to periodType=Month + referenceDate;
    // response is { period, data } so return the payload (summary object).
    const referenceDate =
      month && year ? `${year}-${String(month).padStart(2, "0")}-01` : null;
    const res = await axiosInstance.get(
      API.workLifeBalance.getSupervisorEmployeeSummary(
        employeeId,
        ReportPeriodTypeEnum.Month,
        referenceDate
      )
    );
    const body = res.data;
    return body && body.data !== undefined ? body.data : body;
  };

  // Manager AI attendance insights for a date range (direct reports). The
  // component manages its own loading so it doesn't block the other tabs.
  const fetchTeamInsights = async (startDate, endDate) => {
    const res = await axiosInstance.get(
      API.attendance.supervisorTeamAttendanceInsights(startDate, endDate)
    );
    return res.data;
  };

  return {
    loading,
    fetchToday,
    fetchTeamMonth,
    fetchTeamWLB,
    fetchEmployeeAttendance,
    fetchEmployeeWLB,
    fetchTeamInsights,
  };
}
